import { useState, useEffect } from "react";
import DelhiveryTransitModal from "@/components/DelhiveryTransitModal";
import { Package, Search, Download, MapPin, RefreshCw, Loader2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import StatusBadge from "@/components/shared/StatusBadge";
import api, { API } from "@/utils/api";
import { toast } from "sonner";

const STATUS_OPTIONS = ["all", "booked", "picked_up", "in_transit", "out_for_delivery", "delivered", "cancelled"];

export default function AllShipments() {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [syncingId, setSyncingId] = useState(null);

  const downloadLabel = async (s) => {
    try {
      const token = localStorage.getItem("sendtoo_token");
      const r = await fetch(`${API}/v2/labels/${s.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch { toast.error("Label download failed"); }
  };

  const sync3PL = async (s) => {
    setSyncingId(s.id);
    try {
      const lr = s.order_id || s.lr_number || s.tracking_id;
      const r = await api.post(`/v2/tracking/sync-3pl/${lr}`);
      toast.success(`Synced ${r.data.synced_count} events`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setSyncingId(null); }
  };

  const is3pl = (s) => ["DELHIVERY_B2B", "DELHIVERY_B2C"].includes((s.assigned_carrier || s.network_type || "").toUpperCase());
  const [manifestingId, setManifestingId] = useState(null);
  const [transitAwb, setTransitAwb] = useState(null);
  const displayAwb = (s) => {
    return s.awb || s.awb_number || "—";
  };
  const needsManifest = (s) => (s.assigned_carrier || "").toUpperCase().startsWith("DELHIVERY") && !(s.awb || s.awb_number);
  const triggerManifest = async (s) => {
    setManifestingId(s.id);
    try {
      const r = await api.post(`/v2/assignments/${s.id}/manifest`);
      toast.success(`Manifested! AWB: ${r.data.awb}`);
      fetchShipments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Manifestation failed — please retry");
    } finally {
      setManifestingId(null);
    }
  };
    useEffect(() => { fetchShipments(); }, [statusFilter]);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (search) params.search = search;
      const res = await api.get("/shipments", { params });
      setShipments(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">All Shipments</h1>
          <p className="text-slate-500 text-sm">{total} total shipments</p>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4 flex flex-wrap gap-3 items-center">
          <form onSubmit={(e) => { e.preventDefault(); fetchShipments(); }} className="flex gap-2 flex-1 min-w-[200px]">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tracking ID, city..."
                data-testid="admin-shipments-search"
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
            </div>
            <button type="submit" className="px-4 py-2 bg-brand-500 text-white text-sm font-semibold rounded-sm">Search</button>
          </form>
          <div className="flex gap-2 flex-wrap">
            {STATUS_OPTIONS.map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)} data-testid={`admin-filter-${s}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full capitalize transition-colors ${statusFilter === s ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                {s === "all" ? "All" : s.replace(/_/g, " ")}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading...</div>
          ) : shipments.length === 0 ? (
            <div className="p-12 text-center"><Package className="w-12 h-12 text-slate-200 mx-auto mb-3" /><p className="text-slate-500">No shipments found.</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr>
                    {["Order ID", "AWB", "From", "To", "Weight", "Price", "Payment", "Current Status", "Date", "Actions"].map((h) => (
                      <th key={h} className="px-2 py-2 text-xs font-bold text-slate-500 uppercase text-left whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {shipments.map((s) => (
                    <tr key={s.id} data-testid="admin-all-shipment-row" className="hover:bg-slate-50">
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => navigate(`/admin/shipments/${s.id}/tracking`)}
                            data-testid={`admin-tracking-link-${s.tracking_id}`}
                            className="font-heading font-bold text-brand-500 text-xs hover:underline"
                          >
                            {s.order_id || s.tracking_id}
                          </button>
                          {s.eway_bill_required && !s.eway_bill_verified && (
                            <span
                              title="Eway Bill required — not yet added"
                              data-testid={`eway-warn-${s.tracking_id}`}
                              className="text-amber-600 text-xs"
                            >
                              ⚠️
                            </span>
                          )}
                          {s.eway_bill_verified && (
                            <span
                              title="Eway Bill verified"
                              data-testid={`eway-ok-${s.tracking_id}`}
                              className="text-emerald-600 text-xs font-bold"
                            >
                              ✓
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-600 font-mono">
                        {needsManifest(s) ? (
                          <button
                            onClick={() => triggerManifest(s)}
                            disabled={manifestingId === s.id}
                            data-testid={`manifest-btn-${s.id}`}
                            className="text-xs font-bold px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                          >
                            {manifestingId === s.id ? "Manifesting..." : "Manifest"}
                          </button>
                        ) : is3pl(s) && (s.awb || s.awb_number) ? (
                          <button
                            onClick={() => setTransitAwb(s.awb || s.awb_number)}
                            className="text-indigo-600 hover:underline font-semibold"
                            data-testid={`awb-link-${s.id}`}
                          >
                            {displayAwb(s)}
                          </button>
                        ) : (
                          displayAwb(s)
                        )}
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-600">{s.origin?.city}</td>
                      <td className="px-2 py-2 text-xs text-slate-600">{s.destination?.city}</td>
                      <td className="px-2 py-2 text-xs text-slate-600">{s.package?.weight}kg</td>
                      <td className="px-2 py-2 text-xs font-semibold">₹{s.price}</td>
                      <td className="px-2 py-2">
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${s.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                          {s.payment_status}
                        </span>
                      </td>
                      <td className="px-2 py-2"><StatusBadge status={s.status} /></td>
                      <td className="px-2 py-2 text-xs text-slate-400 whitespace-nowrap">{new Date(s.created_at).toLocaleDateString("en-IN")}</td>
                      <td className="px-2 py-2">
                        <div className="flex gap-1">
                          <button
                            onClick={() => navigate(`/admin/shipments/${s.id}/tracking`)}
                            title="Tracking timeline"
                            className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded"
                            data-testid={`track-icon-${s.id}`}
                          >
                            <MapPin className="w-4 h-4" />
                          </button>
                          {(s.awb || s.awb_number) && (
                            <button
                              onClick={() => downloadLabel(s)}
                              title="Download label"
                              className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded"
                              data-testid={`label-icon-${s.id}`}
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          )}
                          {is3pl(s) && (
                            <button
                              onClick={() => sync3PL(s)}
                              disabled={syncingId === s.id}
                              title="Sync 3PL"
                              className="p-1.5 text-slate-500 hover:text-blue-700 hover:bg-blue-50 rounded disabled:opacity-50"
                              data-testid={`sync-icon-${s.id}`}
                            >
                              {syncingId === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    <DelhiveryTransitModal awb={transitAwb} open={!!transitAwb} onClose={() => setTransitAwb(null)} />
    </DashboardLayout>
  );
}
